import React, { useMemo, useRef, useState } from 'react'
import { isEmpty } from 'lodash'

import ExternalServicesContext from 'contexts/ExternalServicesContext'

import useAuth from 'hooks/useAuth'
import useFetch from 'hooks/useFetch'

import * as service from 'service'
import constants from 'constants/index'

const { CATALOG, REACHABILITY } = constants.externalServices

// A verificação é preguiçosa: dispara quando alguma superfície pergunta por um
// serviço, e vale pela sessão.
const ExternalServicesProvider = ({ children }) => {
  const { user } = useAuth()

  // Espera a sessão existir: este provider também renderiza na tela de login, e
  // ali o GET tomaria 401 sem nunca repetir — o login navega por history.push,
  // sem reload. Falha segue ignorada de propósito: vale o catálogo local.
  const { response } = useFetch(
    service.dponet.externalServices.get,
    undefined,
    [user],
    !!user,
    true,
  )

  // O catálogo local é o ponto de partida e a rede de segurança: até a resposta
  // chegar, e para sempre se ela falhar, é ele que responde.
  const catalog = useMemo(() => {
    const externalServices = response?.data?.externalServices

    if (isEmpty(externalServices)) return CATALOG

    return externalServices.reduce(
      (merged, { key, ...fields }) => ({
        ...merged,
        [key]: { ...merged[key], ...fields },
      }),
      CATALOG,
    )
  }, [response])

  const [reachabilityByKey, setReachabilityByKey] = useState({})

  // A view e o alerta costumam perguntar pela mesma key no mesmo render.
  const requested = useRef({})

  const setReachability = (key, reachability) =>
    setReachabilityByKey((prev) => ({ ...prev, [key]: reachability }))

  const runProbe = async (key) => {
    const serviceEntry = catalog[key]

    // Fora do catálogo é desconhecido, não inalcançável: fechar o gate aqui
    // seria fechá-lo por ignorância.
    if (!serviceEntry) return REACHABILITY.UNVERIFIABLE

    setReachability(key, REACHABILITY.CHECKING)

    const { probe } = service.externalServices.reachability

    // Uma retentativa evita alerta falso num blip de rede.
    let reachability = await probe(serviceEntry)
    if (reachability !== REACHABILITY.REACHABLE) {
      reachability = await probe(serviceEntry)
    }

    setReachability(key, reachability)

    return reachability
  }

  const recheck = (key) => {
    requested.current[key] = true

    return runProbe(key)
  }

  const probeOnce = (key) => {
    if (!requested.current[key]) recheck(key)
  }

  const markUnreachable = (key) =>
    setReachability(key, REACHABILITY.UNREACHABLE)

  return (
    <ExternalServicesContext.Provider
      value={{
        catalog,
        reachabilityByKey,
        probeOnce,
        recheck,
        markUnreachable,
      }}
    >
      {children}
    </ExternalServicesContext.Provider>
  )
}

export default ExternalServicesProvider
